// Property Onboarding Intake + Lease Builder — the "new rental" workflow
// that follows the LandlordRentals page. Both live at /admin/landlord/
// but on per-rental subpaths so the URL tells you which unit you're on.
//
//   /admin/landlord/rentals/:id/intake  → contractor dispatch (key retrieval,
//                                          lockbox install, move-in/out walks)
//   /admin/landlord/rentals/:id/lease   → PA/NJ/DE lease builder → send-for-sign
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useParams as _up } from "react-router-dom";
import axios from "axios";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast, Toaster } from "sonner";
import { ContextBanner } from "@/pages/landlord/ComplianceLayer";
import {
  Key, House, Sparkle, ArrowLeft, PaperPlaneTilt, CheckCircle, Trash,
  FilePdf, ClipboardText, Wrench, Copy as CopyIcon, MapPinLine, User, Warning,
} from "@phosphor-icons/react";

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const inputCls = "w-full h-11 px-3 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium";
const _API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

// ═════════════════════════════════════════════════════════════
// PROPERTY ONBOARDING INTAKE
// ═════════════════════════════════════════════════════════════
export function LandlordIntake() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [pick, setPick] = useState({
    services: [], vendor_id: "", contractor_name: "", contractor_phone: "",
    lockbox_code: "", scheduled_for: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [dispatchingId, setDispatchingId] = useState(null);

  const load = async () => {
    try {
      const [i, v] = await Promise.all([
        api.get(`/landlord-auto/rentals/${id}/intake`),
        api.get("/landlord-auto/vendors"),
      ]);
      setData(i.data); setVendors(v.data.vendors || []);
    } catch (e) { toast.error("Could not load intake"); nav("/admin/landlord/rentals"); }
  };
  useEffect(() => { load(); }, [id]);

  const toggleService = (k) => {
    setPick(p => ({ ...p, services: p.services.includes(k) ? p.services.filter(x => x !== k) : [...p.services, k] }));
  };
  const applySuggested = (partner) => {
    setPick(p => ({ ...p, vendor_id: "", contractor_name: partner.name, contractor_phone: partner.phone }));
    toast.success(`Filled ${partner.name} as contractor`);
  };

  const dispatchAll = async (taskIds) => {
    for (const tid of taskIds) {
      try { await api.post(`/landlord-auto/intake-tasks/${tid}/dispatch`); }
      catch { /* keep going — individual failures are shown after load() */ }
    }
  };

  const create = async () => {
    if (pick.services.length === 0) { toast.error("Pick at least one service"); return; }
    if (!pick.vendor_id && !(pick.contractor_name && pick.contractor_phone)) {
      toast.error("Pick a vendor from your book, or type a contractor name + phone");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/landlord-auto/rentals/${id}/intake`, {
        services: pick.services,
        contractor_vendor_id: pick.vendor_id || null,
        contractor_name: pick.vendor_id ? null : pick.contractor_name,
        contractor_phone: pick.vendor_id ? null : pick.contractor_phone,
        lockbox_code: pick.lockbox_code || null,
        scheduled_for: pick.scheduled_for || null,
        notes: pick.notes || null,
      });
      toast.success(`${r.data.task_ids.length} task${r.data.task_ids.length === 1 ? "" : "s"} created — dispatching now…`);
      await dispatchAll(r.data.task_ids);
      setPick({ services: [], vendor_id: "", contractor_name: "", contractor_phone: "", lockbox_code: "", scheduled_for: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Create failed")); }
    finally { setSaving(false); }
  };

  const dispatchOne = async (tid) => {
    setDispatchingId(tid);
    try {
      const r = await api.post(`/landlord-auto/intake-tasks/${tid}/dispatch`);
      const st = r.data.dispatch?.sms_status;
      if (st === "sent") toast.success("Contractor texted");
      else if (st === "stubbed") toast.warning("Twilio not wired — SMS logged (configure in Settings › SMS)");
      else toast.error("SMS failed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Dispatch failed")); }
    finally { setDispatchingId(null); }
  };

  const complete = async (tid) => {
    const note = window.prompt("Note about how the task was completed (optional):");
    if (note === null) return;
    try { await api.post(`/landlord-auto/intake-tasks/${tid}/complete`, { completion_note: note || null }); load(); toast.success("Marked done"); }
    catch { toast.error("Update failed"); }
  };
  const drop = async (tid) => {
    if (!window.confirm("Delete this intake task?")) return;
    try { await api.delete(`/landlord-auto/intake-tasks/${tid}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (!data) return <AdminShell><div className="p-6 text-slate-500">Loading intake…</div></AdminShell>;

  return (
    <AdminShell>
      <div className="p-4 md:p-6" data-testid="landlord-intake-page">
        <Link to="/admin/landlord/rentals" className="text-xs text-sky-600 hover:text-sky-800 font-black uppercase tracking-widest inline-flex items-center gap-1 mb-3" data-testid="intake-back-link"><ArrowLeft size={12}/> Rentals</Link>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="h-11 w-11 rounded-xl bg-sky-600 text-white flex items-center justify-center"><Key weight="fill" size={22}/></div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-black text-3xl leading-none">Property Onboarding</h1>
            <div className="text-xs text-slate-500 mt-1">{data.rental.unit_label} · {data.rental.address}</div>
          </div>
          <Link to={`/admin/landlord/rentals/${id}/lease`} data-testid="intake-lease-link"
            className="h-11 px-4 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow">
            <ClipboardText weight="fill" size={14}/> Lease Builder
          </Link>
        </div>

        {/* Suggested Philly partners */}
        {data.suggested_partners.length > 0 && (
          <div className="mb-4 p-4 rounded-2xl bg-emerald-50 border-2 border-emerald-300" data-testid="intake-partners-panel">
            <div className="text-[10px] uppercase font-black tracking-widest text-emerald-800 mb-2 inline-flex items-center gap-1">
              <MapPinLine weight="fill" size={12}/> Recommended for Philly properties
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {data.suggested_partners.map(p => (
                <button key={p.name} type="button" onClick={() => applySuggested(p)} data-testid={`intake-partner-${p.name.replace(/\W+/g,'-').toLowerCase()}`}
                  className="text-left p-3 border-2 border-emerald-200 bg-white hover:border-emerald-500 rounded-xl transition">
                  <div className="font-bold text-sm">{p.name}</div>
                  <div className="text-xs text-emerald-800 font-bold">{p.phone}</div>
                  <div className="text-[11px] text-slate-600 mt-1">{p.notes}</div>
                </button>
              ))}
            </div>
            <div className="text-[10px] text-emerald-800 mt-2 italic">
              Outside Philly? Add your own contractor below — same dispatch flow.
            </div>
          </div>
        )}

        {/* New intake form */}
        <div className="bg-white border-2 border-sky-500 rounded-2xl p-4 md:p-5 mb-5 shadow-lg" data-testid="intake-form">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">1. Which tasks?</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {(data.services || []).map(svc => (
              <button key={svc.key} type="button"
                data-testid={`intake-service-${svc.key}`}
                onClick={() => toggleService(svc.key)}
                className={`text-left p-3 border-2 rounded-xl transition ${pick.services.includes(svc.key) ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white hover:border-sky-300"}`}>
                <div className="flex items-start gap-2">
                  <div className={`w-4 h-4 rounded border-2 mt-0.5 flex-shrink-0 ${pick.services.includes(svc.key) ? "bg-sky-500 border-sky-500" : "border-slate-300"}`}>
                    {pick.services.includes(svc.key) && <CheckCircle weight="fill" size={12} className="text-white"/>}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-sm">{svc.label}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{svc.sms_hint}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">2. Who's going?</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <Field label="Pick from your Vendor Book">
              <select value={pick.vendor_id} onChange={e => setPick(p => ({ ...p, vendor_id: e.target.value }))} data-testid="intake-vendor-select" className={inputCls}>
                <option value="">— free-text below —</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name} · {v.phone}</option>)}
              </select>
            </Field>
            <Field label="Contractor name (free-text)">
              <input value={pick.contractor_name} onChange={e => setPick(p => ({ ...p, contractor_name: e.target.value, vendor_id: "" }))} placeholder="ProCall / LCV / Your out-of-market handyman" data-testid="intake-contractor-name" className={inputCls}/>
            </Field>
            <Field label="Contractor phone">
              <input value={pick.contractor_phone} onChange={e => setPick(p => ({ ...p, contractor_phone: e.target.value, vendor_id: "" }))} placeholder="215-555-0175" data-testid="intake-contractor-phone" className={inputCls}/>
            </Field>
            <Field label="Target date (optional)">
              <input type="date" value={pick.scheduled_for} onChange={e => setPick(p => ({ ...p, scheduled_for: e.target.value }))} data-testid="intake-scheduled" className={inputCls}/>
            </Field>
            {pick.services.includes("lockbox_temporary") && (
              <div className="md:col-span-2">
                <Field label="Temp lockbox combo (sent in SMS)">
                  <input value={pick.lockbox_code} onChange={e => setPick(p => ({ ...p, lockbox_code: e.target.value }))} placeholder="e.g. 2024" data-testid="intake-lockbox-code" className={inputCls}/>
                </Field>
              </div>
            )}
            <div className="md:col-span-2">
              <Field label="Extra notes for the contractor (optional)">
                <textarea rows={2} value={pick.notes} onChange={e => setPick(p => ({ ...p, notes: e.target.value }))} data-testid="intake-notes" className="w-full px-3 py-2 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/>
              </Field>
            </div>
          </div>
          <button onClick={create} disabled={saving} data-testid="intake-create-btn"
            className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 shadow disabled:opacity-50">
            <PaperPlaneTilt weight="fill" size={14}/> {saving ? "Creating & Dispatching…" : `Create ${pick.services.length || ""} Task${pick.services.length === 1 ? "" : "s"} + Text Contractor`}
          </button>
        </div>

        {/* Existing tasks */}
        <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Active intake tasks</div>
        {(data.tasks || []).length === 0 ? (
          <div className="p-6 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl" data-testid="intake-empty">
            No tasks yet — pick services above and dispatch a contractor.
          </div>
        ) : (
          <div className="space-y-2">
            {data.tasks.map(t => (
              <div key={t.id} data-testid={`intake-task-${t.id}`} className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                        t.status === "completed" ? "bg-emerald-100 text-emerald-800" :
                        t.status === "dispatched" ? "bg-sky-100 text-sky-800" :
                        "bg-fuchsia-100 text-fuchsia-800"
                      }`}>{t.status}</span>
                      <span className="font-bold text-sm">{t.label}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {t.contractor_name || "(no contractor)"}{t.contractor_phone ? ` · ${t.contractor_phone}` : ""}
                      {t.scheduled_for ? ` · target ${t.scheduled_for}` : ""}
                    </div>
                    {t.lockbox_code && <div className="text-[11px] font-mono text-slate-600 mt-0.5">Combo: {t.lockbox_code}</div>}
                    {t.dispatch?.sms_body && (
                      <div className="mt-2 p-2 rounded bg-slate-50 border-l-4 border-sky-300 text-xs font-mono">{t.dispatch.sms_body}</div>
                    )}
                    {t.completion_note && (
                      <div className="mt-2 p-2 rounded bg-emerald-50 border-l-4 border-emerald-500 text-xs text-emerald-900"><b>Completed:</b> {t.completion_note}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {t.status !== "completed" && (
                      <>
                        <button onClick={() => dispatchOne(t.id)} disabled={dispatchingId === t.id}
                          data-testid={`intake-dispatch-${t.id}`}
                          className="h-8 px-3 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 disabled:opacity-50">
                          <PaperPlaneTilt weight="fill" size={10}/> {t.status === "dispatched" ? "Re-send" : "Text Now"}
                        </button>
                        <button onClick={() => complete(t.id)} data-testid={`intake-complete-${t.id}`}
                          className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                          <CheckCircle weight="fill" size={10}/> Done
                        </button>
                      </>
                    )}
                    <button onClick={() => drop(t.id)} data-testid={`intake-delete-${t.id}`}
                      className="h-8 px-3 bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-500 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                      <Trash size={10}/> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}

// ═════════════════════════════════════════════════════════════
// LEASE BUILDER
// ═════════════════════════════════════════════════════════════
export function LandlordLeaseBuilder() {
  const { id } = useParams();
  const [rental, setRental] = useState(null);
  const [states, setStates] = useState([]);
  const [lease, setLease] = useState({
    state: "PA",
    start_date: "",
    end_date: "",
    security_deposit_cents: 0,
    pet_policy: "No pets without prior written consent.",
    late_fee_policy: "",
    utilities_note: "Renter pays gas, electric, and internet. Landlord pays water/sewer + trash.",
    custom_clauses: [],
  });
  const [signUrl, setSignUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [addClause, setAddClause] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    try {
      const [l, t] = await Promise.all([
        api.get(`/landlord-auto/rentals/${id}/lease`),
        api.get("/landlord-auto/lease-templates"),
      ]);
      setRental(l.data.rental); setStates(t.data.states || []);
      if (l.data.lease && Object.keys(l.data.lease).length > 0) {
        setLease(x => ({ ...x, ...l.data.lease, security_deposit_cents: l.data.lease.security_deposit_cents || 0 }));
        if (l.data.lease.sign_token) {
          setSignUrl(`${window.location.origin}/rent-lease/${l.data.lease.sign_token}`);
        }
      } else {
        // Default deposit = 1 month rent
        setLease(x => ({ ...x, security_deposit_cents: l.data.rental.rent_monthly_cents || 0 }));
      }
    } catch { toast.error("Could not load lease"); }
  };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    if (!lease.start_date || !lease.end_date) { toast.error("Start + end dates required"); return; }
    setSaving(true);
    try {
      await api.post(`/landlord-auto/rentals/${id}/lease`, {
        state: lease.state,
        start_date: lease.start_date,
        end_date: lease.end_date,
        security_deposit_cents: parseInt(lease.security_deposit_cents, 10) || 0,
        pet_policy: lease.pet_policy || null,
        late_fee_policy: lease.late_fee_policy || null,
        utilities_note: lease.utilities_note || null,
        custom_clauses: lease.custom_clauses || [],
      });
      toast.success("Lease saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setSaving(false); }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("access_token");
    window.open(`${API}/landlord-auto/rentals/${id}/lease.pdf?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  const sendSign = async () => {
    try {
      const r = await api.post(`/landlord-auto/rentals/${id}/lease/send-sign`, {});
      setSignUrl(r.data.sign_url);
      toast.success("Sign link ready — copy and text/email it to your renter");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed")); }
  };

  const aiSuggestClauses = async () => {
    setAiLoading(true); setAiSuggestions(null);
    try {
      const r = await api.post(`/landlord-auto/rentals/${id}/lease/ai-clauses`, { property_type: "apartment" });
      setAiSuggestions(r.data.clauses || []);
      if (r.data.from_cache) toast.success("Suggestions loaded from cache");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI clauses failed")); }
    finally { setAiLoading(false); }
  };
  const acceptClause = (text) => {
    if (!text || (lease.custom_clauses || []).includes(text)) return;
    setLease(x => ({ ...x, custom_clauses: [...(x.custom_clauses || []), text] }));
    setAiSuggestions(s => (s || []).filter(c => c.text !== text));
    toast.success("Clause added");
  };

  const stateMeta = states.find(s => s.code === lease.state);

  if (!rental) return <AdminShell><div className="p-6 text-slate-500">Loading lease builder…</div></AdminShell>;

  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="landlord-lease-page">
        <Link to="/admin/landlord/rentals" className="text-xs text-sky-600 hover:text-sky-800 font-black uppercase tracking-widest inline-flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Rentals</Link>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="h-11 w-11 rounded-xl bg-slate-900 text-white flex items-center justify-center"><ClipboardText weight="fill" size={22}/></div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-black text-3xl leading-none">Lease Builder</h1>
            <div className="text-xs text-slate-500 mt-1">{rental.unit_label} · {rental.address}</div>
          </div>
          <Link to={`/admin/landlord/rentals/${id}/intake`} data-testid="lease-intake-link"
            className="h-11 px-4 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow">
            <Key weight="fill" size={14}/> Onboarding Intake
          </Link>
        </div>

        {lease.status && (
          <div className="mb-4 p-2 rounded-lg bg-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-700 inline-flex items-center gap-2" data-testid="lease-status">
            Lease status: <span className={
              lease.status === "signed" ? "text-emerald-700" :
              lease.status === "sent_for_sign" ? "text-sky-700" : "text-slate-500"
            }>{lease.status.replace(/_/g, " ")}</span>
            {lease.signature?.signed_at && <span className="text-slate-500 normal-case">by {lease.signature.typed_name} · {lease.signature.signed_at.slice(0, 10)}</span>}
          </div>
        )}

        <ContextBanner variant="amber" title="Legal review before signing"
          testid="lease-legal-banner">
          This is a starter lease template. Field Radar is not your attorney. Have a
          {" "}<b>PA-licensed lawyer</b> review the executed document — especially for
          Philadelphia rentals, which require a valid <b>L&I Rental License</b> and
          <b> Certificate of Rental Suitability</b> before the lease is enforceable.
        </ContextBanner>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-6 shadow-sm space-y-4" data-testid="lease-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="State">
              <select value={lease.state} onChange={e => setLease(x => ({ ...x, state: e.target.value }))} data-testid="lease-state" className={inputCls}>
                {states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Start date">
              <input type="date" value={lease.start_date} onChange={e => setLease(x => ({ ...x, start_date: e.target.value }))} data-testid="lease-start" className={inputCls}/>
            </Field>
            <Field label="End date">
              <input type="date" value={lease.end_date} onChange={e => setLease(x => ({ ...x, end_date: e.target.value }))} data-testid="lease-end" className={inputCls}/>
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Security deposit ($)">
              <input type="number" min="0" step="1" value={(lease.security_deposit_cents / 100).toString()}
                onChange={e => setLease(x => ({ ...x, security_deposit_cents: Math.round(parseFloat(e.target.value || "0") * 100) }))}
                data-testid="lease-deposit" className={inputCls}/>
            </Field>
            <div className="p-3 rounded-lg bg-amber-50 border-l-4 border-amber-500 text-xs text-amber-900">
              <b>{stateMeta?.name} security rules:</b> {stateMeta?.security_note}
            </div>
          </div>

          <Field label="Utilities / responsibility">
            <textarea rows={2} value={lease.utilities_note} onChange={e => setLease(x => ({ ...x, utilities_note: e.target.value }))} data-testid="lease-utilities" className="w-full px-3 py-2 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/>
          </Field>
          <Field label="Late fee policy">
            <textarea rows={2} value={lease.late_fee_policy} onChange={e => setLease(x => ({ ...x, late_fee_policy: e.target.value }))}
              placeholder={stateMeta?.late_fee_note}
              data-testid="lease-late-fee" className="w-full px-3 py-2 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/>
            <div className="text-[10px] text-amber-700 mt-1">{stateMeta?.late_fee_note}</div>
          </Field>
          <Field label="Pet policy">
            <textarea rows={2} value={lease.pet_policy} onChange={e => setLease(x => ({ ...x, pet_policy: e.target.value }))} data-testid="lease-pets" className="w-full px-3 py-2 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/>
          </Field>

          <div>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500">Additional clauses</div>
              <button type="button" onClick={aiSuggestClauses} disabled={aiLoading}
                data-testid="lease-ai-suggest-btn"
                className="h-8 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow disabled:opacity-50">
                <Sparkle weight="fill" size={11}/> {aiLoading ? "Thinking…" : "AI Suggest for This Address"}
              </button>
            </div>

            {aiSuggestions && aiSuggestions.length > 0 && (
              <div className="mb-3 p-3 rounded-xl bg-fuchsia-50 border-2 border-fuchsia-300" data-testid="lease-ai-suggestions">
                <div className="text-[10px] uppercase font-black tracking-widest text-fuchsia-800 mb-1.5 inline-flex items-center gap-1">
                  <Sparkle weight="fill" size={11}/> AI-suggested clauses ({stateMeta?.name})
                </div>
                <div className="space-y-1.5">
                  {aiSuggestions.map((c, i) => (
                    <div key={i} data-testid={`lease-ai-suggestion-${i}`} className="p-2 bg-white border border-fuchsia-200 rounded-lg flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700">{c.title}</div>
                        <div className="text-xs text-slate-700 mt-0.5">{c.text}</div>
                      </div>
                      <button type="button" onClick={() => acceptClause(c.text)}
                        data-testid={`lease-ai-accept-${i}`}
                        className="h-7 px-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[9px] rounded inline-flex items-center gap-1 flex-shrink-0">
                        + Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5" data-testid="lease-clause-list">
              {(lease.custom_clauses || []).map((cl, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex-1 p-2 border border-slate-200 rounded-lg text-sm bg-slate-50">{cl}</div>
                  <button type="button" onClick={() => setLease(x => ({ ...x, custom_clauses: x.custom_clauses.filter((_, j) => j !== i) }))}
                    data-testid={`lease-clause-remove-${i}`}
                    className="h-8 w-8 rounded-lg bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 inline-flex items-center justify-center">
                    <Trash size={12}/>
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input value={addClause} onChange={e => setAddClause(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && addClause.trim()) { setLease(x => ({ ...x, custom_clauses: [...x.custom_clauses, addClause.trim()] })); setAddClause(""); e.preventDefault(); } }}
                placeholder="Type a clause and press Enter (e.g. 'No smoking anywhere on the premises.')"
                data-testid="lease-clause-input"
                className="flex-1 h-10 px-3 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/>
              <button type="button" onClick={() => { if (addClause.trim()) { setLease(x => ({ ...x, custom_clauses: [...x.custom_clauses, addClause.trim()] })); setAddClause(""); } }}
                data-testid="lease-clause-add"
                className="h-10 px-3 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase tracking-widest text-xs rounded-lg">Add</button>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-200">
            <button onClick={save} disabled={saving} data-testid="lease-save-btn"
              className="h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow disabled:opacity-50">
              {saving ? "Saving…" : "Save Draft"}
            </button>
            <button onClick={downloadPdf} data-testid="lease-pdf-btn"
              className="h-11 px-5 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow">
              <FilePdf weight="fill" size={14}/> Download PDF
            </button>
            <button onClick={sendSign} data-testid="lease-send-sign-btn"
              className="h-11 px-5 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow">
              <PaperPlaneTilt weight="fill" size={14}/> Send for E-Sign
            </button>
          </div>

          {signUrl && (
            <div className="mt-2 p-3 rounded-lg bg-sky-50 border-2 border-sky-300 flex items-center gap-2 flex-wrap" data-testid="lease-sign-url">
              <div className="flex-1 min-w-[200px] font-mono text-[11px] text-sky-800 truncate">{signUrl}</div>
              <button onClick={async () => { try { await navigator.clipboard.writeText(signUrl); toast.success("Copied — text/email it to your renter"); } catch { toast.error("Copy failed"); } }}
                data-testid="lease-sign-copy"
                className="h-9 px-3 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                <CopyIcon weight="fill" size={11}/> Copy
              </button>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

// ═════════════════════════════════════════════════════════════
// APPLICATIONS INBOX (admin — prospect apps + AI screening)
// ═════════════════════════════════════════════════════════════
const TRIAGE_META = {
  GREEN: { color: "bg-emerald-500 text-white", label: "GREEN" },
  YELLOW: { color: "bg-amber-500 text-white", label: "YELLOW" },
  RED: { color: "bg-red-500 text-white", label: "RED" },
};

export function LandlordApplications() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [screening, setScreening] = useState(null);
  const [deciding, setDeciding] = useState(null);

  const load = async () => {
    try { const r = await api.get(`/landlord-auto/rentals/${id}/applications`); setData(r.data); }
    catch { toast.error("Could not load applications"); }
  };
  useEffect(() => { load(); }, [id]);

  const mintLink = async () => {
    try {
      const r = await api.post(`/landlord-auto/rentals/${id}/application-link`);
      toast.success("Application link ready — post it on Craigslist/Zillow");
      load();
    } catch { toast.error("Could not mint link"); }
  };

  const copy = async () => {
    if (!data?.apply_url) return;
    try { await navigator.clipboard.writeText(data.apply_url); toast.success("Application link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const screen = async (aid) => {
    setScreening(aid);
    try {
      const r = await api.post(`/landlord-auto/applications/${aid}/screen`);
      toast.success(`AI: ${r.data.screening?.triage} · income ${r.data.screening?.income_ratio}x`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI screen failed")); }
    finally { setScreening(null); }
  };

  const decide = async (aid, decision) => {
    if (decision === "approved" && !window.confirm("Approve this applicant and convert them into the active renter on this rental? (Replaces any existing renter info.)")) return;
    setDeciding(aid);
    try {
      const r = await api.post(`/landlord-auto/applications/${aid}/decision`, { decision });
      toast.success(r.data.converted ? "Approved — set as active renter" : `Marked ${decision}`);
      load();
    } catch { toast.error("Decision failed"); }
    finally { setDeciding(null); }
  };

  if (!data) return <AdminShell><div className="p-6 text-slate-500">Loading applications…</div></AdminShell>;
  const rent = (data.rental?.rent_monthly_cents || 0) / 100;

  return (
    <AdminShell>
      <div className="p-4 md:p-6" data-testid="landlord-applications-page">
        <Link to="/admin/landlord/rentals" className="text-xs text-sky-600 hover:text-sky-800 font-black uppercase tracking-widest inline-flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Rentals</Link>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="h-11 w-11 rounded-xl bg-fuchsia-600 text-white flex items-center justify-center"><User weight="fill" size={22}/></div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-black text-3xl leading-none">Applications</h1>
            <div className="text-xs text-slate-500 mt-1">{data.rental?.unit_label} · {data.rental?.address} · Rent {dollars(data.rental?.rent_monthly_cents)}</div>
          </div>
        </div>

        {/* Public apply URL */}
        <ContextBanner variant="sky" title="Fair Housing + Fair Chance Housing"
          testid="apps-fair-housing-banner">
          AI screening is a triage aid — not a decision. You cannot deny an applicant
          based on race, color, religion, national origin, sex, age, disability, familial
          status, sexual orientation, source of income (Section 8 vouchers are protected),
          or arrests that did not lead to conviction. For Philly rentals, criminal-history
          look-back is limited to 7 years (felony) / 4 years (misdemeanor).
        </ContextBanner>

        <div className="mb-4 p-4 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid="applications-apply-url">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Public application link</div>
          {data.apply_url ? (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex-1 min-w-[240px] px-3 py-2 bg-sky-50 border border-sky-200 rounded-lg font-mono text-[11px] text-sky-800 truncate" title={data.apply_url}>
                {data.apply_url}
              </div>
              <button onClick={copy} data-testid="applications-copy-link"
                className="h-9 px-3 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                <CopyIcon weight="fill" size={12}/> Copy
              </button>
              <a href={data.apply_url} target="_blank" rel="noreferrer" data-testid="applications-open-link"
                className="h-9 px-3 bg-white hover:bg-slate-100 border-2 border-slate-200 text-slate-700 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                Open
              </a>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="text-xs text-slate-500 flex-1">No public link yet. Mint one to accept applications.</div>
              <button onClick={mintLink} data-testid="applications-mint-btn"
                className="h-9 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-lg">
                Mint Link
              </button>
            </div>
          )}
          <div className="text-[10px] text-slate-400 mt-2">Post on Craigslist / Zillow / Facebook Marketplace. Applications land in the inbox below.</div>
        </div>

        {(data.applications || []).length === 0 ? (
          <div className="p-6 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl" data-testid="applications-empty">
            No applications yet — share your link to start collecting.
          </div>
        ) : (
          <div className="space-y-3">
            {data.applications.map(a => {
              const s = a.screening;
              const triage = s?.triage && TRIAGE_META[s.triage];
              const income = (a.monthly_income_cents || 0) / 100;
              const ratio = rent > 0 ? (income / rent).toFixed(2) : "—";
              const decided = a.decision?.decision;
              return (
                <div key={a.id} data-testid={`application-row-${a.id}`}
                  className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {triage && <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${triage.color}`}>{triage.label}</span>}
                        {decided && <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${decided === "approved" ? "bg-emerald-100 text-emerald-700" : decided === "rejected" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{decided}</span>}
                        <span className="font-bold text-lg">{a.applicant_name}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {a.applicant_email}{a.applicant_phone && ` · ${a.applicant_phone}`}
                      </div>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div><div className="text-[9px] uppercase font-black tracking-widest text-slate-400">Income</div><div className="font-bold">${income.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</div></div>
                        <div><div className="text-[9px] uppercase font-black tracking-widest text-slate-400">Ratio</div><div className={`font-bold ${ratio < 2 ? "text-red-600" : ratio < 2.5 ? "text-amber-600" : "text-emerald-600"}`}>{ratio}x</div></div>
                        <div><div className="text-[9px] uppercase font-black tracking-widest text-slate-400">Credit</div><div className="font-bold">{a.credit_score_range || "—"}</div></div>
                        <div><div className="text-[9px] uppercase font-black tracking-widest text-slate-400">Move-in</div><div className="font-bold">{a.move_in_date || "—"}</div></div>
                      </div>
                      <div className="mt-2 text-xs text-slate-600">
                        Employer: <b>{a.employer || "—"}</b>{a.employer_phone && ` · ${a.employer_phone}`}
                        {a.occupants && <> · Occupants: <b>{a.occupants}</b></>}
                        {a.pets && <> · Pets: <b>{a.pets}</b></>}
                      </div>
                      {a.prior_landlord_name && (
                        <div className="text-xs text-slate-600">Prior landlord: <b>{a.prior_landlord_name}</b>{a.prior_landlord_phone && ` · ${a.prior_landlord_phone}`}</div>
                      )}
                      {a.notes && <div className="text-xs text-slate-500 mt-1 italic">"{a.notes}"</div>}

                      {/* Screening panel */}
                      {s && (
                        <div className="mt-3 p-3 rounded-lg bg-slate-50 border-l-4 border-fuchsia-500" data-testid={`application-screen-${a.id}`}>
                          <div className="text-[10px] uppercase font-black tracking-widest text-fuchsia-700 mb-1">AI Screening</div>
                          {s.one_line_summary && <div className="text-sm text-slate-800 font-bold">{s.one_line_summary}</div>}
                          {(s.positives || []).length > 0 && (
                            <div className="mt-2">
                              <div className="text-[10px] uppercase font-black tracking-widest text-emerald-700 mb-0.5">Positives</div>
                              <ul className="list-disc pl-5 text-xs text-slate-700">{s.positives.map((p, i) => <li key={i}>{p}</li>)}</ul>
                            </div>
                          )}
                          {(s.red_flags || []).length > 0 && (
                            <div className="mt-2">
                              <div className="text-[10px] uppercase font-black tracking-widest text-red-700 mb-0.5">Red flags</div>
                              <ul className="list-disc pl-5 text-xs text-red-800">{s.red_flags.map((p, i) => <li key={i}>{p}</li>)}</ul>
                            </div>
                          )}
                          {(s.follow_up_questions || []).length > 0 && (
                            <div className="mt-2">
                              <div className="text-[10px] uppercase font-black tracking-widest text-sky-700 mb-0.5">Follow-up questions</div>
                              <ul className="list-disc pl-5 text-xs text-sky-900">{s.follow_up_questions.map((p, i) => <li key={i}>{p}</li>)}</ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-1.5 flex-shrink-0 min-w-[160px]">
                      <button onClick={() => screen(a.id)} disabled={screening === a.id}
                        data-testid={`application-screen-btn-${a.id}`}
                        aria-label={s ? "Re-run AI screening on this application" : "Run AI screening on this application"}
                        className={`h-9 px-3 ${s ? "bg-slate-800 hover:bg-slate-900" : "bg-fuchsia-600 hover:bg-fuchsia-700"} text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center justify-center gap-1 shadow disabled:opacity-50`}>
                        <Sparkle weight="fill" size={12}/> {screening === a.id ? "Screening…" : (s ? "Re-screen" : "AI Screen")}
                      </button>
                      {!decided && (
                        <>
                          <button onClick={() => decide(a.id, "approved")} disabled={deciding === a.id}
                            data-testid={`application-approve-${a.id}`}
                            aria-label={`Approve application from ${a.applicant_name}`}
                            className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center justify-center gap-1 shadow disabled:opacity-50">
                            <CheckCircle weight="fill" size={12}/> Approve
                          </button>
                          <button onClick={() => decide(a.id, "rejected")} disabled={deciding === a.id}
                            data-testid={`application-reject-${a.id}`}
                            aria-label={`Reject application from ${a.applicant_name}`}
                            className="h-9 px-3 bg-white hover:bg-red-50 border-2 border-red-200 text-red-700 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center justify-center gap-1">
                            Reject
                          </button>
                        </>
                      )}
                      {decided && a.decision?.decision === "rejected" && (
                        <button onClick={() => decide(a.id, "approved")} disabled={deciding === a.id}
                          data-testid={`application-approve-${a.id}`}
                          aria-label={`Overturn — approve ${a.applicant_name}`}
                          className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center justify-center gap-1 shadow">
                          <CheckCircle weight="fill" size={12}/> Approve Anyway
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminShell>
  );
}

// ═════════════════════════════════════════════════════════════
// PUBLIC APPLICATION FORM  /apply/:token
// ═════════════════════════════════════════════════════════════
export function RenterApplication() {
  const { token } = _up();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    applicant_name: "", applicant_email: "", applicant_phone: "",
    current_address: "", dob: "",
    employer: "", employer_phone: "",
    monthly_income: "", credit_score_range: "not sure",
    move_in_date: "", occupants: 1, pets: "",
    prior_landlord_name: "", prior_landlord_phone: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${_API}/public/rent-portal/apply/${token}`);
      setData(r.data);
    } catch (e) { setErr(e?.response?.status === 404 ? "This application link isn't active." : "Something went wrong."); }
  };
  useEffect(() => { load(); }, [token]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.applicant_name || !form.applicant_email) { toast.error("Name and email required"); return; }
    if (!form.monthly_income) { toast.error("Monthly income required for screening"); return; }
    setBusy(true);
    try {
      await axios.post(`${_API}/public/rent-portal/apply/${token}`, {
        ...form,
        monthly_income_cents: Math.round(parseFloat(form.monthly_income || "0") * 100),
        occupants: parseInt(form.occupants, 10) || 1,
      });
      setDone(true);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Submit failed")); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6"><div className="text-center"><Warning size={48} className="text-amber-400 mx-auto mb-3"/><div className="font-display font-black text-xl">{err}</div></div></div>;
  if (!data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50 py-6" data-testid="renter-application">
      <Toaster position="top-center" richColors/>
      <div className="max-w-lg mx-auto px-5">
        <div className="bg-slate-900 text-white rounded-2xl p-5 mb-4">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">Rental Application</div>
          <div className="font-display font-black text-2xl">{data.unit_label}</div>
          <div className="text-sm text-slate-400">{data.address}</div>
          <div className="text-xs text-slate-500 mt-3">
            {dollars(data.rent_monthly_cents)}/mo · Managed by <span className="font-bold text-slate-200">{data.landlord_name}</span>
          </div>
        </div>

        {done ? (
          <div className="bg-white rounded-2xl p-6 shadow-md text-center" data-testid="renter-application-done">
            <CheckCircle weight="fill" size={40} className="text-emerald-500 mx-auto mb-2"/>
            <div className="font-display font-black text-xl">Application submitted</div>
            <div className="text-sm text-slate-500 mt-2">Your landlord got it and will be in touch. Keep an eye on your email + phone.</div>
          </div>
        ) : (
          <>
            {(data.disclosures || []).length > 0 && (
              <div className="mb-4 space-y-2" data-testid="renter-application-disclosures">
                {data.disclosures.map((d, i) => (
                  <div key={i} className="p-3 rounded-lg bg-amber-50 border-l-4 border-amber-500 text-xs text-amber-900">
                    <div className="font-black uppercase tracking-widest text-[10px] mb-1">{d.title}</div>
                    <div>{d.text}</div>
                  </div>
                ))}
              </div>
            )}
          <form onSubmit={submit} className="bg-white rounded-2xl p-5 shadow-md space-y-3" data-testid="renter-application-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Full name *"><input value={form.applicant_name} onChange={e => setF("applicant_name", e.target.value)} data-testid="app-name" className={inputCls} required/></Field>
              <Field label="Email *"><input type="email" value={form.applicant_email} onChange={e => setF("applicant_email", e.target.value)} data-testid="app-email" className={inputCls} required/></Field>
              <Field label="Phone"><input value={form.applicant_phone} onChange={e => setF("applicant_phone", e.target.value)} data-testid="app-phone" className={inputCls}/></Field>
              <Field label="Date of birth"><input type="date" value={form.dob} onChange={e => setF("dob", e.target.value)} data-testid="app-dob" className={inputCls}/></Field>
              <div className="md:col-span-2"><Field label="Current address"><input value={form.current_address} onChange={e => setF("current_address", e.target.value)} data-testid="app-current-addr" className={inputCls}/></Field></div>
              <Field label="Employer"><input value={form.employer} onChange={e => setF("employer", e.target.value)} data-testid="app-employer" className={inputCls}/></Field>
              <Field label="Employer phone"><input value={form.employer_phone} onChange={e => setF("employer_phone", e.target.value)} data-testid="app-employer-phone" className={inputCls}/></Field>
              <Field label="Monthly income ($) *"><input type="number" min="0" step="100" value={form.monthly_income} onChange={e => setF("monthly_income", e.target.value)} data-testid="app-income" className={inputCls} required/></Field>
              <Field label="Credit score range">
                <select value={form.credit_score_range} onChange={e => setF("credit_score_range", e.target.value)} data-testid="app-credit" className={inputCls}>
                  {(data.credit_ranges || []).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Move-in date"><input type="date" value={form.move_in_date} onChange={e => setF("move_in_date", e.target.value)} data-testid="app-move-in" className={inputCls}/></Field>
              <Field label="# of occupants"><input type="number" min="1" max="20" value={form.occupants} onChange={e => setF("occupants", e.target.value)} data-testid="app-occupants" className={inputCls}/></Field>
              <div className="md:col-span-2"><Field label="Pets (breed + weight)"><input value={form.pets} onChange={e => setF("pets", e.target.value)} placeholder="1 cat, or none" data-testid="app-pets" className={inputCls}/></Field></div>
              <Field label="Prior landlord name"><input value={form.prior_landlord_name} onChange={e => setF("prior_landlord_name", e.target.value)} data-testid="app-prior-landlord" className={inputCls}/></Field>
              <Field label="Prior landlord phone"><input value={form.prior_landlord_phone} onChange={e => setF("prior_landlord_phone", e.target.value)} data-testid="app-prior-landlord-phone" className={inputCls}/></Field>
              <div className="md:col-span-2"><Field label="Anything else your landlord should know?"><textarea rows={3} value={form.notes} onChange={e => setF("notes", e.target.value)} data-testid="app-notes" className="w-full px-3 py-2 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/></Field></div>
            </div>
            <button type="submit" disabled={busy} data-testid="app-submit"
              className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-sm rounded-xl inline-flex items-center justify-center gap-2 shadow-lg disabled:opacity-60">
              {busy ? "Submitting…" : "Submit Application"}
            </button>
            <div className="text-[10px] text-slate-400 text-center">Your info goes only to your prospective landlord. You'll hear back within a few days.</div>
          </form>
          </>
        )}
      </div>
    </div>
  );
}

export function RenterLeaseSign() {
  const { token } = _up();
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${_API}/public/rent-portal/lease-sign/${token}`);
      setData(r.data);
      if (r.data?.renter_name && !name) setName(r.data.renter_name);
    } catch (e) { setErr(e?.response?.status === 404 ? "Sign link isn't active." : "Something went wrong."); }
  };
  useEffect(() => { load(); }, [token]);

  const sign = async () => {
    if (!name || name.trim().length < 2) { toast.error("Type your full name to sign"); return; }
    setBusy(true);
    try {
      await axios.post(`${_API}/public/rent-portal/lease-sign/${token}`, { typed_name: name.trim() });
      toast.success("Signed — your landlord has been notified.");
      load();
    } catch { toast.error("Sign failed"); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6"><div className="text-center"><ClipboardText size={48} className="text-amber-400 mx-auto mb-3"/><div className="font-display font-black text-xl">{err}</div></div></div>;
  if (!data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400">Loading lease…</div>;

  const l = data.lease || {};
  const signed = l.status === "signed";

  return (
    <div className="min-h-screen bg-slate-50 py-6" data-testid="renter-lease-sign">
      <div className="max-w-xl mx-auto px-5">
        <div className="bg-slate-900 text-white rounded-2xl p-5 mb-4">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">Residential Lease</div>
          <div className="font-display font-black text-2xl">{data.unit_label}</div>
          <div className="text-sm text-slate-400">{data.address}</div>
          <div className="text-xs text-slate-500 mt-3">Landlord: <span className="text-slate-200 font-bold">{data.landlord_name}</span></div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[9px] uppercase font-black tracking-widest text-slate-400">Term</div><div className="font-bold">{l.start_date} → {l.end_date}</div></div>
            <div><div className="text-[9px] uppercase font-black tracking-widest text-slate-400">Monthly rent</div><div className="font-bold">{dollars(data.rent_monthly_cents)}</div></div>
            <div><div className="text-[9px] uppercase font-black tracking-widest text-slate-400">Rent due on</div><div className="font-bold">Day {data.rent_due_day}</div></div>
            <div><div className="text-[9px] uppercase font-black tracking-widest text-slate-400">Security deposit</div><div className="font-bold">{dollars(l.security_deposit_cents)}</div></div>
          </div>
          <div className="text-xs text-slate-600 border-t border-slate-100 pt-3 space-y-2">
            {l.utilities_note && <div><b className="text-slate-800">Utilities:</b> {l.utilities_note}</div>}
            {l.late_fee_policy && <div><b className="text-slate-800">Late fees:</b> {l.late_fee_policy}</div>}
            {l.pet_policy && <div><b className="text-slate-800">Pets:</b> {l.pet_policy}</div>}
            {(l.custom_clauses || []).map((c, i) => <div key={i}><b className="text-slate-800">Clause {i+1}:</b> {c}</div>)}
          </div>

          {signed ? (
            <div className="p-3 rounded-lg bg-emerald-50 border-l-4 border-emerald-500 text-emerald-900" data-testid="renter-lease-signed">
              <div className="font-bold text-sm">✓ Signed by {l.signature?.typed_name}</div>
              <div className="text-xs">on {l.signature?.signed_at?.slice(0, 10)}</div>
            </div>
          ) : (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Type your full name to sign</div>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="First and last name"
                data-testid="renter-lease-name-input"
                className="w-full h-12 px-3 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-black text-lg italic"/>
              <button onClick={sign} disabled={busy} data-testid="renter-lease-sign-btn"
                className="mt-3 w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-sm rounded-xl inline-flex items-center justify-center gap-2 shadow-lg disabled:opacity-60">
                <CheckCircle weight="fill" size={16}/> {busy ? "Signing…" : "Sign Lease"}
              </button>
              <div className="text-[10px] text-slate-400 text-center mt-2">By typing your name and tapping Sign, you agree to the terms above. Your landlord will receive a signed copy.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
